import { useFormik } from "formik";
import { observer } from "mobx-react-lite";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { ProgressBar } from "primereact/progressbar";
import { classNames } from "primereact/utils";
import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import { RootStoreContext } from "../../../../app/stores/rootStore";
import SmilesView from "../../SmilesView/SmilesView";

// Basic SMILES syntax validation (non-empty, balanced brackets, balanced ring numbers)
const isValidSmiles = (s) => {
  if (!s || s.trim().length === 0) return false;
  let brackets = 0;
  for (const ch of s) {
    if (ch === '[') brackets++;
    if (ch === ']') brackets--;
    if (brackets < 0) return false;
  }
  if (brackets !== 0) return false;
  const ringDigits = s.match(/%\d{2}|\d/g);
  if (ringDigits) {
    const counts = {};
    for (const d of ringDigits) {
      counts[d] = (counts[d] || 0) + 1;
    }
    if (Object.values(counts).some((c) => c % 2 !== 0)) return false;
  }
  return /[A-Za-z0-9]/.test(s);
};

const CompoundEvolutionAddNew = ({ closeSidebar }) => {
  const rootStore = useContext(RootStoreContext);
  const {
    addingCompoundEvolution,
    addCompoundEvolution,
    selectedProject,
    loadingProject,
    fetchCompoundEvolution,
  } = rootStore.projectStore;

  const [smilesPreview, setSmilesPreview] = useState("");
  const [smilesRenderError, setSmilesRenderError] = useState(false);

  const formik = useFormik({
    initialValues: {
      smile: "",
      molWeight: "",
      molArea: "",
      externalCompoundIds: "",
      notes: "",
    },
    validate: (data) => {
      let errors = {};
      if (!data.smile) {
        errors.smile = "SMILES is required.";
      } else if (!isValidSmiles(data.smile)) {
        errors.smile = "Invalid SMILES — unbalanced brackets or ring numbers.";
      }
      if (!data.notes) {
        errors.notes = "A note is required.";
      }
      return errors;
    },
    onSubmit: (data) => {
      data["projectId"] = selectedProject.id;
      addCompoundEvolution(data).then((res) => {
        if (res !== null) {
          toast.success("Compound added!");
          closeSidebar();
          fetchCompoundEvolution(selectedProject.id);
          formik.resetForm();
          setSmilesPreview("");
          setSmilesRenderError(false);
        } else {
          toast.error("Failed to add compound.");
        }
      });
    },
  });

  const isFormFieldValid = (field) =>
    !!(formik.touched[field] && formik.errors[field]);
  const getFormErrorMessage = (field) => {
    return (
      isFormFieldValid(field) && (
        <small className="p-error">{formik.errors[field]}</small>
      )
    );
  };

  if (!addingCompoundEvolution && !loadingProject) {
    const handleSmilesChange = (e) => {
      formik.handleChange(e);
      const val = e.target.value;
      if (val && val.length > 0 && val !== smilesPreview) {
        setSmilesPreview(val);
        setSmilesRenderError(false);
      } else if (!val) {
        setSmilesPreview("");
        setSmilesRenderError(false);
      }
    };

    const handleSmilesBlur = () => {
      if (formik.values.smile && formik.values.smile !== smilesPreview) {
        setSmilesPreview(formik.values.smile);
        setSmilesRenderError(false);
      }
    };

    return (
      <div className="flex flex-column w-full">
        <div className="card w-full">
          <form onSubmit={formik.handleSubmit} className="p-fluid">
            {/* SMILES Input with live preview */}
            <div className="field">
              <label htmlFor="smile" className={classNames({ "p-error": isFormFieldValid("smile") })}>
                SMILES *
              </label>
              <InputTextarea
                id="smile"
                value={formik.values.smile}
                onChange={handleSmilesChange}
                onBlur={handleSmilesBlur}
                rows={2}
                className={classNames({ "p-invalid": isFormFieldValid("smile") })}
                placeholder="e.g. CCO, c1ccccc1, Cc1ccccc1C(=O)O"
                autoResize
              />
              {getFormErrorMessage("smile")}
            </div>

            {/* Live SMILES Preview */}
            {smilesPreview && (
              <div className="flex justify-content-center mb-3 p-2 border-1 border-round surface-border" style={{ background: "#fafafa" }}>
                {smilesRenderError ? (
                  <div className="text-center p-3 text-color-secondary">
                    <i className="pi pi-exclamation-triangle mr-2" />
                    Could not render structure — check SMILES syntax
                  </div>
                ) : (
                  <SmilesView smiles={smilesPreview} width={180} height={150} />
                )}
              </div>
            )}

            {/* External Compound ID */}
            <div className="field">
              <label htmlFor="externalCompoundIds">External Compound ID</label>
              <InputText
                id="externalCompoundIds"
                type="text"
                value={formik.values.externalCompoundIds}
                onChange={formik.handleChange}
                placeholder="e.g. CCO-001, CHEMBL123"
              />
            </div>

            {/* Mol Weight + Mol Area side by side */}
            <div className="grid">
              <div className="field col-6">
                <label htmlFor="molWeight">Mol Weight</label>
                <InputText id="molWeight" type="decimal" value={formik.values.molWeight} onChange={formik.handleChange} placeholder="e.g. 46.07" />
              </div>
              <div className="field col-6">
                <label htmlFor="molArea">Mol Area</label>
                <InputText id="molArea" type="decimal" value={formik.values.molArea} onChange={formik.handleChange} placeholder="e.g. 45.0" />
              </div>
            </div>

            {/* Notes */}
            <div className="field">
              <label htmlFor="notes" className={classNames({ "p-error": isFormFieldValid("notes") })}>
                Notes *
              </label>
              <InputTextarea
                id="notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                autoResize rows={3}
                className={classNames({ "p-invalid": isFormFieldValid("notes") })}
                placeholder="Why is this compound being added? e.g. New hit from HTS campaign"
              />
              {getFormErrorMessage("notes")}
            </div>

            {/* Buttons */}
            <div className="flex justify-content-end gap-2 mt-3">
              <Button label="Cancel" icon="pi pi-times" type="button" className="p-button-text" onClick={closeSidebar} />
              <Button icon="pi pi-check" type="submit" label="Add Compound" loading={addingCompoundEvolution} />
            </div>
          </form>
        </div>
      </div>
    );
  }

  return <ProgressBar mode="indeterminate" style={{ height: "6px" }} />;
};

export default observer(CompoundEvolutionAddNew);
